import { useEffect, useRef, useState } from "react";
import i18n from "../../../language/i18n";
import { getBrandFileUrlByPathAndFileName } from "../../../utilization/StorageUtilization";
import { audioPlayerStyles } from "./AudioPlayerStyles";

const AudioPlayer = ({
  audioFileList,
  currentAudioIndex,
  onAudioIndexChange,
  onTimeUpdate,
  onPlayStateChange,
  isExternallyPaused,
  seekToMs,
}) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const playbackRates = [0.5, 0.75, 1, 1.25, 1.5, 2];

  const currentAudioFile = audioFileList[currentAudioIndex];
  // An item may carry a ready-made `url` INSTEAD of a brand path/name pair (group
  // assignment materials arrive that way — an external link, or a file DTO already
  // resolved to a URL). The pair stays the default so existing callers are untouched.
  const audioUrl = !currentAudioFile
    ? null
    : currentAudioFile.url
      ? currentAudioFile.url
      : getBrandFileUrlByPathAndFileName(
          currentAudioFile.brandPath,
          currentAudioFile.brandFileName
        );

  useEffect(() => {
    if (isExternallyPaused && audioRef.current && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      onPlayStateChange(false);
    }
  }, [isExternallyPaused]);

  useEffect(() => {
    if (seekToMs && audioRef.current) {
      const seekTime = seekToMs.ms / 1000;
      audioRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);
      onTimeUpdate(seekToMs.ms);
      if (!isPlaying) {
        startPlayback();
      }
    }
  }, [seekToMs]);


  /**
   * play() is async, and it REJECTS for an unplayable source - a 404, a codec the browser will not
   * decode, an autoplay block. Setting isPlaying before it settles left the pause glyph showing
   * over a clip that never started, and the unhandled rejection surfaced in the dev overlay.
   */
  const startPlayback = () => {
    if (!audioRef.current) return;
    const playback = audioRef.current.play();
    // play() returns a promise in every current browser, but not in all of them and not in jsdom,
    // where it returns undefined. Treat a non-promise as "started" rather than reaching for .then
    // on it.
    if (!playback || typeof playback.then !== "function") {
      setIsPlaying(true);
      onPlayStateChange(true);
      return;
    }
    playback
      .then(() => {
        setIsPlaying(true);
        onPlayStateChange(true);
      })
      .catch(() => {
        setIsPlaying(false);
        onPlayStateChange(false);
      });
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      onPlayStateChange(false);
    } else {
      startPlayback();
    }
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    const timeMs = Math.floor(audioRef.current.currentTime * 1000);
    setCurrentTime(audioRef.current.currentTime);
    onTimeUpdate(timeMs);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;
    const seekTime = parseFloat(e.target.value);
    audioRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
    onTimeUpdate(Math.floor(seekTime * 1000));
  };

  const handleEnded = () => {
    setIsPlaying(false);
    onPlayStateChange(false);
  };

  const handlePlaybackRateChange = () => {
    const currentIndex = playbackRates.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % playbackRates.length;
    const newRate = playbackRates[nextIndex];
    setPlaybackRate(newRate);
    if (audioRef.current) {
      audioRef.current.playbackRate = newRate;
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (!currentAudioFile) return null;

  return (
    <div className="qbx-root">
      <style>{audioPlayerStyles}</style>
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
      />
      <div className="qbx-row">
        <button className="qbx-play-btn" onClick={handlePlayPause}>
          {isPlaying ? "⏸" : "▶"}
        </button>
        <button className="qbx-rate-btn" onClick={handlePlaybackRateChange}>
          {playbackRate}x
        </button>
        <div className="qbx-slider-wrap">
          <input
            type="range"
            min="0"
            max={duration || 0}
            step="0.1"
            value={currentTime}
            onChange={handleSeek}
            className="qbx-slider"
          />
          <div className="qbx-time-row">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
      </div>
      {audioFileList.length > 1 && (
        <div className="qbx-nav">
          <button
            className="qbx-nav-btn"
            disabled={currentAudioIndex === 0}
            onClick={() => onAudioIndexChange(currentAudioIndex - 1)}
          >
            &#8592;
          </button>
          <span className="qbx-counter">
            {currentAudioIndex + 1} / {audioFileList.length}
          </span>
          <button
            className="qbx-nav-btn"
            disabled={currentAudioIndex === audioFileList.length - 1}
            onClick={() => onAudioIndexChange(currentAudioIndex + 1)}
          >
            &#8594;
          </button>
        </div>
      )}
    </div>
  );
};

export default AudioPlayer;
